/**
 * P34 (Feb 27, 2026) — Rhinobill-style claims filter rail.
 *
 * Lives on the left of the claims table. Top section = the most-used
 * filters always visible; "More Filters" expands the EDI 277 + ICN +
 * Created-date row. Includes Saved Views (Phase 3) at the top.
 */
import React, { useState } from 'react';
import { CaretDown, CaretRight, BookmarkSimple, FloppyDisk, Trash, XCircle } from '@phosphor-icons/react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const STATUS_OPTIONS = [
  { value: 'Draft',           label: 'Draft' },
  { value: 'Pending',         label: 'Pending' },
  { value: 'Submitted',       label: 'Submitted' },
  { value: 'Accepted',        label: 'Accepted' },
  { value: 'Rejected',        label: 'Rejected' },
  { value: 'Fixed',           label: 'Fixed' },
  { value: 'Fix Successful',  label: 'Fix Successful' },
  { value: 'Partially Paid',  label: 'Partially Paid' },
  { value: 'Paid',            label: 'Paid' },
  { value: 'Denied',          label: 'Denied' },
  { value: 'Voided',          label: 'Voided' },
];

const ALL_VALUE = '__ALL__'; // shadcn Select can't use empty string for value

/** Wrapper for shadcn Select that lets us treat empty string as "All". */
const FilterSelect = ({ value, onChange, placeholder, testid, children }) => (
  <Select
    value={value || ALL_VALUE}
    onValueChange={(v) => onChange(v === ALL_VALUE ? '' : v)}
  >
    <SelectTrigger data-testid={testid}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="max-h-72">
      <SelectItem value={ALL_VALUE} data-testid={`${testid}-all`}>{placeholder}</SelectItem>
      {children}
    </SelectContent>
  </Select>
);

const ClaimsFiltersRail = (props) => {
  const {
    // Filter state + setters
    search, setSearch,
    filterStatus, setFilterStatus,
    filterPayer, setFilterPayer,
    filterProcedure, setFilterProcedure,
    filterServiceFrom, setFilterServiceFrom,
    filterServiceTo, setFilterServiceTo,
    filterClaimNumber, setFilterClaimNumber,
    filterIcn, setFilterIcn,
    filterCreatedFrom, setFilterCreatedFrom,
    filterCreatedTo, setFilterCreatedTo,
    filterEdi277Category, setFilterEdi277Category,
    filterEdi277Code, setFilterEdi277Code,
    filterEdi277Entity, setFilterEdi277Entity,
    // Dropdown options + counters
    filterOptions = { payers: [], procedures: [], edi_277: { categories: [], codes: [], entities: [] } },
    activeFilterCount = 0,
    onClear,
    // Saved views
    savedViews = [],
    onSaveCurrentView,
    onApplySavedView,
    onDeleteSavedView,
  } = props;

  const [moreOpen, setMoreOpen] = useState(false);
  const [viewNameDraft, setViewNameDraft] = useState('');

  return (
    <Card className="border-slate-200 sticky top-4" data-testid="claims-filters-rail">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Filter Claims</h3>
          {activeFilterCount > 0 ? (
            <button
              onClick={onClear}
              className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1"
              data-testid="clear-filters-btn"
            >
              <XCircle size={14} /> Clear ({activeFilterCount})
            </button>
          ) : null}
        </div>

        {/* Patient search */}
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Filter By Patient</Label>
          <Input
            type="text"
            placeholder="Name or Medicaid ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="filter-patient-input"
          />
        </div>

        {/* Payer — carrier-level dropdown (P41) showing
            "{carrier} ({plan type})" e.g. "Anthem (Managed Care)". */}
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Payer</Label>
          <FilterSelect
            value={filterPayer}
            onChange={setFilterPayer}
            placeholder="All Payers"
            testid="filter-payer-select"
          >
            {filterOptions.payers.map((p) => (
              <SelectItem key={p.code} value={p.code} data-testid={`payer-option-${p.code}`}>
                {p.label}
                {p.plan_type ? (
                  <span className="ml-1 text-slate-500">({p.plan_type})</span>
                ) : null}
              </SelectItem>
            ))}
          </FilterSelect>
        </div>

        {/* Procedure */}
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Procedure</Label>
          <FilterSelect
            value={filterProcedure}
            onChange={setFilterProcedure}
            placeholder="All Procedures"
            testid="filter-procedure-select"
          >
            {filterOptions.procedures.map((p) => (
              <SelectItem key={p.code} value={p.code} data-testid={`procedure-option-${p.code}`}>
                <span className="font-mono">{p.code}</span>
                {p.is_dodd ? <span className="ml-2 text-[10px] text-indigo-600">DODD</span> : null}
              </SelectItem>
            ))}
          </FilterSelect>
        </div>

        {/* Status */}
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Status</Label>
          <FilterSelect
            value={filterStatus}
            onChange={setFilterStatus}
            placeholder="Any Status"
            testid="filter-status-select"
          >
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`status-option-${s.value}`}>
                {s.label}
              </SelectItem>
            ))}
          </FilterSelect>
        </div>

        {/* Service date range */}
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Service date</Label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={filterServiceFrom}
              onChange={(e) => setFilterServiceFrom(e.target.value)}
              placeholder="From"
              data-testid="filter-service-from"
            />
            <Input
              type="date"
              value={filterServiceTo}
              onChange={(e) => setFilterServiceTo(e.target.value)}
              placeholder="To"
              data-testid="filter-service-to"
            />
          </div>
        </div>

        {/* RB Claim ID */}
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">RB Claim ID</Label>
          <Input
            type="text"
            placeholder="CLM-…"
            value={filterClaimNumber}
            onChange={(e) => setFilterClaimNumber(e.target.value)}
            className="font-mono text-xs"
            data-testid="filter-claim-number"
          />
        </div>

        {/* More Filters toggle */}
        <button
          type="button"
          onClick={() => setMoreOpen((o) => !o)}
          className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 w-full justify-between py-1"
          data-testid="more-filters-toggle"
        >
          <span className="font-semibold">More Filters</span>
          {moreOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
        </button>

        {moreOpen ? (
          <div className="space-y-4 pt-2 border-t border-slate-100" data-testid="more-filters-section">
            {/* ICN / TCN */}
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">TCN / ICN</Label>
              <Input
                type="text"
                placeholder="Payer trace number…"
                value={filterIcn}
                onChange={(e) => setFilterIcn(e.target.value)}
                className="font-mono text-xs"
                data-testid="filter-icn"
              />
            </div>

            {/* Created date range */}
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">Created</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={filterCreatedFrom}
                  onChange={(e) => setFilterCreatedFrom(e.target.value)}
                  data-testid="filter-created-from"
                />
                <Input
                  type="date"
                  value={filterCreatedTo}
                  onChange={(e) => setFilterCreatedTo(e.target.value)}
                  data-testid="filter-created-to"
                />
              </div>
            </div>

            {/* EDI 277 trio */}
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">EDI 277 Category</Label>
              <FilterSelect
                value={filterEdi277Category}
                onChange={setFilterEdi277Category}
                placeholder="Any Category"
                testid="filter-277-category-select"
              >
                {filterOptions.edi_277.categories.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`277-cat-option-${c}`}>{c}</SelectItem>
                ))}
              </FilterSelect>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">EDI 277 Code</Label>
              <FilterSelect
                value={filterEdi277Code}
                onChange={setFilterEdi277Code}
                placeholder="Any Code"
                testid="filter-277-code-select"
              >
                {filterOptions.edi_277.codes.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`277-code-option-${c}`}>{c}</SelectItem>
                ))}
              </FilterSelect>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">EDI 277 Entity Code</Label>
              <FilterSelect
                value={filterEdi277Entity}
                onChange={setFilterEdi277Entity}
                placeholder="Any Entity"
                testid="filter-277-entity-select"
              >
                {filterOptions.edi_277.entities.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`277-ent-option-${c}`}>{c}</SelectItem>
                ))}
              </FilterSelect>
            </div>
          </div>
        ) : null}

        {/* Saved Views */}
        <div className="pt-3 border-t border-slate-100" data-testid="saved-views-section">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500 inline-flex items-center gap-1">
              <BookmarkSimple size={12} /> Saved Views
            </Label>
          </div>
          {savedViews.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic" data-testid="saved-views-empty">
              No saved views yet — save the current filter combo below.
            </p>
          ) : (
            <ul className="space-y-1 mb-2 max-h-32 overflow-y-auto" data-testid="saved-views-list">
              {savedViews.map((v) => (
                <li key={v.id} className="flex items-center justify-between text-xs group">
                  <button
                    type="button"
                    onClick={() => onApplySavedView(v)}
                    className="text-indigo-700 hover:text-indigo-900 truncate text-left flex-1"
                    title={v.name}
                    data-testid={`saved-view-apply-${v.id}`}
                  >
                    {v.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteSavedView(v)}
                    className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700 ml-2"
                    title="Delete view"
                    data-testid={`saved-view-delete-${v.id}`}
                  >
                    <Trash size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-1">
            <Input
              type="text"
              placeholder="Name this view…"
              value={viewNameDraft}
              onChange={(e) => setViewNameDraft(e.target.value)}
              className="text-xs h-8"
              data-testid="saved-view-name-input"
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => {
                if (!viewNameDraft.trim()) return;
                onSaveCurrentView(viewNameDraft.trim());
                setViewNameDraft('');
              }}
              disabled={!viewNameDraft.trim() || activeFilterCount === 0}
              data-testid="saved-view-save-btn"
              title={activeFilterCount === 0 ? 'Apply a filter first' : 'Save this filter combo'}
            >
              <FloppyDisk size={12} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClaimsFiltersRail;
